import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2 } from 'lucide-react';
import { groupService, type ServiceContext } from '@/services';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const groupFormSchema = z.object({
  name: z.string().min(1, 'Name is required').max(255),
});

type GroupFormValues = z.infer<typeof groupFormSchema>;

const defaultValues: GroupFormValues = { name: '' };

type SaveAction = 'close' | 'new' | 'open';

interface NewGroupDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  context: ServiceContext;
  onCreated: () => void;
}

export default function NewGroupDialog({
  open,
  onOpenChange,
  context,
  onCreated,
}: NewGroupDialogProps) {
  const navigate = useNavigate();
  const [isSaving, setIsSaving] = useState(false);

  const form = useForm<GroupFormValues>({
    resolver: zodResolver(groupFormSchema),
    defaultValues,
  });

  useEffect(() => {
    if (open) {
      form.reset(defaultValues);
    }
  }, [open, form]);

  const handleSave = async (action: SaveAction) => {
    const valid = await form.trigger();
    if (!valid) return;

    const values = form.getValues();
    setIsSaving(true);

    try {
      const result = await groupService.create({ name: values.name }, context);

      if (result.error) {
        toast.error(result.error.message);
        return;
      }

      toast.success(`Group "${values.name}" created`);
      onCreated();

      switch (action) {
        case 'close':
          onOpenChange(false);
          break;
        case 'new':
          form.reset(defaultValues);
          break;
        case 'open':
          onOpenChange(false);
          navigate(`/security/groups/${result.data!.id}`);
          break;
      }
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>New Group</DialogTitle>
          <DialogDescription>
            Create a new group to organize users and manage permissions.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleSave('close');
            }}
            className="space-y-4"
          >
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input placeholder="e.g. Accounting Team" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </form>
        </Form>

        <DialogFooter className="flex-col sm:flex-row gap-2 sm:gap-0">
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <div className="flex gap-2 ml-auto">
            <Button
              variant="outline"
              onClick={() => handleSave('new')}
              disabled={isSaving}
            >
              {isSaving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Save &amp; New
            </Button>
            <Button
              variant="outline"
              onClick={() => handleSave('open')}
              disabled={isSaving}
            >
              Save &amp; Open
            </Button>
            <Button
              onClick={() => handleSave('close')}
              disabled={isSaving}
            >
              {isSaving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Save &amp; Close
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
